<template>
  <div class="start-page-bg">
    <div class="container">
      <div class="row align-items-center justify-content-center vh-100">
        
        <div v-if="isLoading" class="text-center text-light">
          <div class="spinner-border" role="status"></div>
          <p class="mt-2">Checking proposal status...</p>
        </div>

        <div v-else class="card col-xl-5 col-md-8 col-lg-6 col-sm-12" style="top: 5%">
          <div class="card-body">
            <div class="text-center justify-content-center mb-4">
              <img src="@/assets/img/vertex_logo.png" alt="vertex_logo" class="img-fluid w-50"
                onerror="this.onerror=null; this.src='https://placehold.co/200x100?text=Vertex+Logo';" />
            </div>

            <div v-if="alreadyDeclined" class="text-center py-4">
              <h3 class="card-title"><strong>Proposal Declined</strong></h3>
              <div class="m-3">
                <h6 class="card-subtitle">
                  This proposal has been declined. <br><br>
                  Thank you for your feedback.
                </h6>
              </div>
            </div>

            <div v-else-if="submitted" class="text-center py-4">
              <h3 class="card-title"><strong>Thank You!</strong></h3>
              <div class="m-3">
                <h6 class="card-subtitle">
                  Thank you for helping us improve our service.
                </h6>
              </div>
            </div>

            <div v-else>
              <div class="text-center justify-content-center">
                <h3 class="card-title"><strong>Proposal Feedback</strong></h3>
              </div>

              <div class="m-3 text-center">
                <h6 class="card-subtitle">
                  We are sorry to hear you won't be proceeding with this proposal.
                  <br><br>
                  We'd appreciate your feedback to help us improve.
                </h6>
              </div>

              <hr />

              <form ref="feedbackFormRef" class="row mx-4 g-0" style="gap: 1.5rem" @submit.prevent="submitFeedback">
                
                <div class="form-group">
                  <label for="declineReason" class="form-label">
                    Why did you choose not to proceed with this proposal?
                    <span class="requiredField">*</span>
                  </label>
                  <VueMultiselect 
                    v-model="feedback.reasons" 
                    :options="declineReasons" 
                    :multiple="true"
                    :close-on-select="false" 
                    placeholder="Select one or more reasons" 
                    label="text" 
                    track-by="value"
                    :class="{ 'please-select-color': feedback.reasons.length > 0 }"
                    @blur="v$.feedback.reasons.$touch">
                  </VueMultiselect>
                  <span class="form-text text-danger error-msg" v-if="v$.feedback.reasons.$error">
                    Please select at least one reason.
                  </span>
                </div>

                <div v-if="isReasonSelected('alternate_provider')" class="form-floating">
                  <input type="text" id="alternateProvider" v-model.trim="feedback.alternateProvider"
                    class="form-control" placeholder="Alternate Provider Name" />
                  <label for="alternateProvider" class="text-muted">Alternate Provider Name</label>
                </div>

                <div v-if="isReasonSelected('other')" class="form-floating">
                  <input type="text" id="otherReason" v-model.trim="feedback.otherReasonDetails"
                    class="form-control" placeholder="Please specify" />
                  <label for="otherReason" class="text-muted">Other Reason Details</label>
                </div>

                <div class="form-floating">
                  <textarea class="form-control" id="otherFeedback" v-model.trim="feedback.otherFeedback"
                    placeholder="Please provide any other feedback" style="height: 150px"></textarea>
                  <label for="otherFeedback" class="text-muted">Other feedback on your experience</label>
                </div>

                <div class="mb-3 text-center">
                  <p v-if="v$.$error" class="form-text text-danger">
                    Please review and fill up all required fields.
                  </p>
                  <button type="submit" class="btn btn-info text-light px-5" :disabled="isProcessing">
                    {{ isProcessing ? "Submitting..." : "Submit Feedback" }}
                  </button>
                </div>
              </form>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useVuelidate } from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import VueMultiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.css";

export default {
  name: "ProposalFeedback",
  components: {
    VueMultiselect,
  },
  props: {
    unique_key: { type: String, required: true },
    access_key: { type: String, required: true },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      isLoading: true,
      isProcessing: false,
      submitted: false,
      alreadyDeclined: false,
      feedback: {
        reasons: [],
        alternateProvider: "",
        otherReasonDetails: "",
        otherFeedback: "",
      },
      declineReasons: [
        { text: "No longer required product or service", value: "no_longer_required" },
        { text: "Went with an alternate provider", value: "alternate_provider" },
        { text: "Too expensive", value: "too_expensive" },
        { text: "Quality or reputation concerns", value: "quality_reputation_concerns" },
        { text: "Internal reasons", value: "internal_reasons" },
        { text: "Other", value: "other" },
      ],
    };
  },
  validations() {
    return {
      feedback: {
        reasons: { required },
      },
    };
  },
  async created() {
    await this.getProposalDetails();
  },
  methods: {
    async getProposalDetails() {
      this.isLoading = true;
      try {
        const response = await this.$axios_internal.get(`/proposal/view-proposal/${this.unique_key}/${this.access_key}`);
        const proposal = response.data.vertex_proposal;

        if (!proposal) {
          this.$router.push({ name: 'ProposalDeletedNonExist' });
          return;
        }

        if (proposal.proposal_feedback != null) {
          this.alreadyDeclined = true;
        }
      } catch (error) {
        console.error("Error fetching details:", error);
        if (error.response && error.response.status === 403 && error.response.data && error.response.data.message === 'access_revoked') {
            this.$router.push({ name: 'ProposalNoAccess' });
        } else {
            this.$router.push({ name: 'ProposalDeletedNonExist' });
        }
      } finally {
        this.isLoading = false;
      }
    },

    isReasonSelected(reasonValue) {
      return this.feedback.reasons.some((reason) => reason.value === reasonValue);
    },

    async submitFeedback() {
      const isFormCorrect = await this.v$.$validate();
      if (!isFormCorrect) return;

      this.isProcessing = true;

      // Construct a single payload with an array of reasons
      const payload = {
        unique_key: this.unique_key,
        access_key: this.access_key,
        feedback: this.feedback.otherFeedback,
        reasons: this.feedback.reasons.map(reason => {
          let extraField = "";
          if (reason.value === 'alternate_provider') {
            extraField = this.feedback.alternateProvider;
          } else if (reason.value === 'other') {
            extraField = this.feedback.otherReasonDetails;
          }

          return {
            chosen_feedback: reason.value,
            extra_field: extraField,
          };
        }),
      };

      try {
        // Send a single request
        await this.$axios_internal.post('/proposals/feedback', payload);
        this.submitted = true;
      } catch (error) {
        console.error("Submission error:", error);
        this.$toast.error("There was an error submitting your feedback.");
      } finally {
        this.isProcessing = false;
      }
    },
  },
};
</script>

<style scoped>
.requiredField {
  color: red;
}

::v-deep .please-select-color .multiselect__placeholder,
::v-deep .please-select-color .multiselect__single {
  color: #fff !important;
}

::v-deep .multiselect__option--highlight {
  background: #00a0c1 !important;
  color: #fff !important;
}

::v-deep .multiselect__option--highlight::after {
  background: #00a0c1 !important;
  color: #fff !important;
}
</style>